import React, { PropsWithChildren, useContext, useEffect, useRef } from "react";
import ResourceSeriesProvider, {
    useResourceSeries,
    useResourceSeriesDispatch,
} from "../../../../../components/Player/ResourceSeries/Contexts";
import { useParams } from "react-router-dom";
import {
    ResourceSeries,
    ResourceSeriesExecutionStage,
} from "../../../../../components/Player/ResourceSeries/types";
import { initializeResourceSeries } from "../../../../../components/Player/ResourceSeries/utils/defaultReducer.utils";
import { dataStore } from "../../../../../contexts/DataContext";
import { getFirstResourceHierarchy } from "../../../../../components/Player/ResourceSeries/utils/defaultReducer.utils";
import { dataToResources } from "../../../../../components/Player/ResourceSeries/utils/moaResourceSeriesUtils";
import useFetchActivityExercisesFromDataJSON from "../../../../../components/Player/ResourceSeries/utils/useFetchActivityExercisesFromDataJSON";

export const MODULES_COLLECTION_ID = "allModules";

const SoloTeacherResourceSeriesProvider = ({ children }: PropsWithChildren) => {
    const { data } = useContext(dataStore);
    let { moduleId, objectiveId } = useParams<{
        moduleId?: string;
        objectiveId?: string;
    }>();
    if (!moduleId) {
        moduleId = data.modules[0].id;
    }

    const initialResourceSeriesRef = useRef<ResourceSeries>();
    if (!initialResourceSeriesRef.current) {
        const { resources, nbItems } = dataToResources(moduleId, data);
        resources[MODULES_COLLECTION_ID] = {
            id: MODULES_COLLECTION_ID,
            type: "set",
            name: "",
            description: "",
            learning_sets: data.modules.map((mod) => mod.id),
            learning_items: undefined,
            learning_set_context: undefined,
            learning_set_type: "modules_collection",
            learning_set_tags: undefined,
            status: undefined,
            created: "",
            updated: "",
        };
        const baseResourceSeries = initializeResourceSeries(
            MODULES_COLLECTION_ID,
            resources,
            { totalItems: nbItems }
        );
        if (!objectiveId) initialResourceSeriesRef.current = baseResourceSeries;
        else {
            const firstItem = getFirstResourceHierarchy(
                objectiveId,
                resources,
                [moduleId]
            );
            initialResourceSeriesRef.current = {
                ...baseResourceSeries,
                hierarchy: firstItem,
                executionStage: resources[firstItem[0]]
                    ? ResourceSeriesExecutionStage.PlayingCurrentExercise
                    : ResourceSeriesExecutionStage.FetchingResource,
            };
        }
    }

    return (
        <ResourceSeriesProvider
            initialResourceSeries={initialResourceSeriesRef.current!}
        >
            <SoloTeacherResourceSeriesSideEffects>
                {children}
            </SoloTeacherResourceSeriesSideEffects>
        </ResourceSeriesProvider>
    );
};
export default SoloTeacherResourceSeriesProvider;

const SoloTeacherResourceSeriesSideEffects = ({
    children,
}: PropsWithChildren) => {
    useFetchActivityExercisesFromDataJSON();

    // Fetch new modules
    const { executionStage, hierarchy, resources } = useResourceSeries();
    const dispatch = useResourceSeriesDispatch();
    const { data } = useContext(dataStore);
    useEffect(() => {
        if (
            executionStage !== ResourceSeriesExecutionStage.FetchingResource ||
            !hierarchy
        )
            return;
        const moduleId = hierarchy[hierarchy.length - 1];
        if (!data.modules.some((mod) => mod.id === moduleId)) return;
        const { resources: nextModule } = dataToResources(moduleId, data);
        dispatch({
            type: "fetch-resources",
            resources: nextModule,
        });
    }, [resources, executionStage, hierarchy, data, dispatch]);

    return <>{children}</>;
};
